import { unwrap } from 'features/fim/hooks'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexFilterOp, type RolodexLoadedEntryJson } from 'types'

import { FIM_ENTRY_PHOTO, type FimPhotoData, type FimPhotoEntry } from './types'

/** Rolodex silently drops rows past `limit`; `truncated` is how callers learn. */
export const SUBJECT_PHOTOS_LOAD_LIMIT = 2000

/**
 * Every attached `fim-photo` entry on the site. Site-scoped load because
 * photo entries carry no organization tag; every consumer shares this one
 * cached fetch.
 */
export function useSiteAttachedPhotos(siteID: string): {
  photos: FimPhotoEntry[]
  truncated: boolean
  refresh: () => Promise<unknown>
} {
  const { data, mutate } = useGetRolodexEntriesByLoadOptions<
    RolodexLoadedEntryJson[]
  >(
    useMemo(
      () => ({
        type: [FIM_ENTRY_PHOTO],
        tags: { site: [siteID] },
        filters: [
          { key: 'status', op: RolodexFilterOp.EQUAL, value: 'attached' },
        ],
        limit: SUBJECT_PHOTOS_LOAD_LIMIT,
      }),
      [siteID]
    )
  )
  const photos = useMemo(() => unwrap<FimPhotoData>(data), [data])
  return {
    photos,
    truncated: (data?.length ?? 0) >= SUBJECT_PHOTOS_LOAD_LIMIT,
    refresh: mutate,
  }
}
